import React from "react";
import { Lock } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { asText, CardShell, formatCompact, LoadingRow, unwrapValue, useCxoResource } from "../shared";
import { getDeviceIconMeta } from "../device-gender-icons";

// Generic Devices tab — "items[].impression_device is Meta's own value
// (iphone, android_smartphone, desktop, ipad...), with impressions_share_pct;
// total_impressions is the centre number." Per direct ask: shown per raw
// category (not grouped into Mobile/Desktop/Tablet/Other) now that each one
// has its own real icon — a bucketed "Mobile" slice couldn't show both the
// iPhone and Android marks at once anyway.
const DONUT_COLORS = ["#4f46e5", "#60a5fa", "#f59e0b", "#a855f7", "#10b981", "#f43f5e", "#0ea5e9"];

const DevicesTab: React.FC<{ basePath: string; entityId: string; baseParams: Record<string, any> }> = ({ basePath, entityId, baseParams }) => {
  const { data, loading } = useCxoResource<any>(basePath, `${entityId}/devices`, baseParams, !!entityId);

  if (loading && !data) return <LoadingRow label="Loading device breakdown…" />;
  const items: any[] = Array.isArray(data?.items) ? data.items : [];
  if (!data || data.available === false || items.length === 0) {
    return (
      <CardShell title="Device breakdown" description="Impressions by device.">
        <div className="flex flex-col items-center justify-center gap-1.5 py-10 text-gray-400 dark:text-gray-500">
          <Lock size={18} />
          <p className="text-sm font-medium">Device data not available</p>
          <p className="text-xs">Device breakdown is not available for this period yet.</p>
        </div>
      </CardShell>
    );
  }

  const grouped = new Map<string, number>();
  items.forEach((it) => {
    const key = asText(it.impression_device).trim().toLowerCase();
    grouped.set(key, (grouped.get(key) || 0) + (Number(unwrapValue(it.impressions_share_pct)) || 0));
  });
  const chartData = Array.from(grouped.entries())
    .map(([key, value]) => ({ key, value, ...getDeviceIconMeta(key) }))
    .sort((a, b) => b.value - a.value);

  return (
    <CardShell title="Device breakdown" description="Impressions by device.">
      <div className="flex items-center gap-4">
        <div className="h-44 w-44 shrink-0 relative">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={chartData} dataKey="value" nameKey="label" innerRadius={42} outerRadius={66} paddingAngle={2}>
                {chartData.map((d, i) => (
                  <Cell key={i} fill={d.color || DONUT_COLORS[i % DONUT_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className="text-base font-bold text-gray-900 dark:text-gray-100">{formatCompact(data.total_impressions, "0")}</span>
            <span className="text-[10px] text-gray-400 dark:text-gray-500">Impressions</span>
          </div>
        </div>
        <div className="space-y-1.5 text-xs flex-1">
          {chartData.map((d) => (
            <div key={d.key} className="flex items-center gap-2">
              {d.Icon ? <d.Icon size={14} className="shrink-0" /> : <span className="w-2 h-2 rounded-full shrink-0 bg-gray-300" />}
              <span className="text-gray-600 dark:text-gray-300 flex-1">{d.label}</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{d.value.toFixed(0)}%</span>
            </div>
          ))}
        </div>
      </div>
    </CardShell>
  );
};

export default DevicesTab;
